import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { api } from "../lib/projects-api.ts";
import { initials } from "../lib/initials.ts";
import TaskDrawer from "../components/TaskDrawer.jsx";

const PRIORITY_COLOR = { high: "error", med: "warning", low: "default" };
const PRIORITIES = ["low", "med", "high"];

const EMPTY_FILTER = { q: "", assignee: "", tag: "", priority: "" };

/**
 * The board: columns left to right, cards top to bottom, drag to move.
 *
 * Columns scroll sideways in their own container so the page body never does
 * (the kit's rule). A move is one write — the dragged card's new column and a
 * fractional order between its neighbours. A viewer on a private project sees
 * the board but none of the controls that would be refused, so `readOnly`
 * hides them rather than letting somebody click into a 403.
 */
export default function Board({ project, board, people, epics, tags = [], me, readOnly = false, onReload,
  initialOpenTaskId, onOpenedInitial }) {
  const [dragId, setDragId] = useState(null);
  const [colDragId, setColDragId] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [filter, setFilter] = useState(EMPTY_FILTER);

  // A search result opens straight to the card it named, the one time the
  // id it was given actually matches a task here.
  useEffect(() => {
    if (!initialOpenTaskId) return;
    const t = board.tasks.find((x) => x.id === initialOpenTaskId);
    if (t) { setOpenTask(t); onOpenedInitial?.(); }
  }, [initialOpenTaskId, board, onOpenedInitial]);

  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const epicById = useMemo(() => new Map(epics.map((e) => [e.id, e])), [epics]);

  const matches = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return (t) =>
      (!q || `${project.key}-${t.number} ${t.title}`.toLowerCase().includes(q))
      && (!filter.assignee || t.assigneeId === filter.assignee)
      && (!filter.priority || t.priority === filter.priority)
      && (!filter.tag || (t.tags || []).includes(filter.tag));
  }, [filter, project.key]);

  const columns = useMemo(() => {
    const tasks = [...board.tasks]
      .filter((t) => !t.parentTaskId)
      .sort((a, b) => a.order.localeCompare(b.order));
    return board.sections.map((s) => ({
      section: s,
      tasks: tasks.filter((t) => t.sectionId === s.id),
    }));
  }, [board]);

  const move = async (sectionId, colTasks, beforeIndex) => {
    if (!dragId) return;
    const dragged = board.tasks.find((t) => t.id === dragId);
    setDragId(null);
    if (!dragged) return;
    const without = colTasks.filter((t) => (t.id === dragId ? false : true));
    const at = beforeIndex == null ? without.length : beforeIndex;
    const after = at > 0 ? without[at - 1].order : "";
    const before = at < without.length ? without[at].order : "";
    if (dragged.sectionId === sectionId && colTasks[at]?.id === dragId) return;
    try {
      await api.updateTask(project.id, dragId, { sectionId, before, after });
      onReload();
    } catch { onReload(); }
  };

  // Drop the dragged column before the target column: a fractional order
  // between the target's left neighbour and the target itself.
  const reorderColumn = async (targetId) => {
    const id = colDragId;
    setColDragId(null);
    if (!id || id === targetId) return;
    const ordered = columns.map((c) => c.section);
    const rest = ordered.filter((s) => s.id !== id);
    const at = rest.findIndex((s) => s.id === targetId);
    if (at < 0) return;
    const after = at > 0 ? rest[at - 1].order : "";
    const before = rest[at].order;
    try { await api.updateSection(project.id, id, { before, after }); onReload(); }
    catch { onReload(); }
  };

  return (
    <>
      <FilterBar filter={filter} setFilter={setFilter} people={people} tags={tags} />

      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1, alignItems: "flex-start" }}>
        {columns.map(({ section, tasks }) => {
          const shown = tasks.filter(matches);
          return (
            <Paper key={section.id} variant="outlined"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (colDragId) reorderColumn(section.id); else move(section.id, tasks, null);
              }}
              sx={{ p: 1.5, minWidth: 280, width: 280, flexShrink: 0, bgcolor: "background.default" }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                {!readOnly ? (
                  <Tooltip title="Drag to reorder columns">
                    <Box component="span" draggable
                      onDragStart={() => setColDragId(section.id)}
                      sx={{ cursor: "grab", color: "text.secondary", fontSize: 14, lineHeight: 1 }}>⠿</Box>
                  </Tooltip>
                ) : null}
                <Typography variant="subtitle2" sx={{ flex: 1 }}>{section.name}</Typography>
                <Chip size="small" label={shown.length === tasks.length ? tasks.length : `${shown.length}/${tasks.length}`} />
                {!readOnly ? (
                  <DeleteColumn project={project} section={section} disabled={tasks.length > 0} onDone={onReload} />
                ) : null}
              </Stack>

              <Stack spacing={1}>
                {shown.map((task, i) => (
                  <Box key={task.id}
                    draggable={!readOnly}
                    onDragStart={() => !readOnly && setDragId(task.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.stopPropagation(); e.preventDefault(); if (!colDragId) move(section.id, shown, i); }}>
                    <Card task={task} project={project} assignee={byId.get(task.assigneeId)}
                      epic={epicById.get(task.epicId)} tags={tags}
                      subtaskCount={board.tasks.filter((t) => t.parentTaskId === task.id).length}
                      onClick={() => setOpenTask(task)} />
                  </Box>
                ))}
                {!readOnly ? <AddCard project={project} sectionId={section.id} onDone={onReload} /> : null}
              </Stack>
            </Paper>
          );
        })}

        {!readOnly ? (
          <Box sx={{ minWidth: 240, flexShrink: 0 }}>
            {addingColumn ? (
              <AddColumn project={project} onDone={() => { setAddingColumn(false); onReload(); }}
                onCancel={() => setAddingColumn(false)} />
            ) : (
              <Button variant="outlined" onClick={() => setAddingColumn(true)}>+ Add column</Button>
            )}
          </Box>
        ) : null}
      </Box>

      {openTask ? (
        <TaskDrawer project={project} task={openTask} people={people} epics={epics}
          sections={board.sections} tags={tags} tasks={board.tasks} me={me} readOnly={readOnly}
          onClose={() => setOpenTask(null)}
          onChanged={() => { setOpenTask(null); onReload(); }} />
      ) : null}
    </>
  );
}

function FilterBar({ filter, setFilter, people, tags }) {
  const set = (k) => (e) => setFilter({ ...filter, [k]: e.target.value });
  const active = filter.q || filter.assignee || filter.tag || filter.priority;
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, alignItems: "center" }}>
      <TextField size="small" placeholder="Search cards" value={filter.q} onChange={set("q")}
        sx={{ minWidth: 200 }} slotProps={{ htmlInput: { "aria-label": "Search cards" } }} />
      <TextField select size="small" label="Assignee" value={filter.assignee} onChange={set("assignee")}
        sx={{ minWidth: 150 }}>
        <MenuItem value="">Anyone</MenuItem>
        {people.map((p) => <MenuItem key={p.id} value={p.id}>{p.displayName || p.emails?.[0] || p.id}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Tag" value={filter.tag} onChange={set("tag")} sx={{ minWidth: 120 }}>
        <MenuItem value="">Any tag</MenuItem>
        {tags.map((t) => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Priority" value={filter.priority} onChange={set("priority")} sx={{ minWidth: 110 }}>
        <MenuItem value="">Any</MenuItem>
        {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
      </TextField>
      {active ? <Button size="small" onClick={() => setFilter(EMPTY_FILTER)}>Clear</Button> : null}
    </Stack>
  );
}

function Card({ task, project, assignee, epic, tags = [], subtaskCount = 0, onClick }) {
  const tagColor = (name) => tags.find((t) => t.name === name)?.color;
  return (
    <Paper variant="outlined" onClick={onClick}
      role="button" tabIndex={0}
      aria-label={`Open ${project.key}-${task.number}: ${task.title}`}
      onKeyDown={(e) => {
        // A card is a div so it can be dragged; without this it is a click
        // target the keyboard cannot reach. Enter and Space open it, like a
        // button would.
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
      }}
      sx={{ p: 1.25, cursor: "pointer", bgcolor: "background.paper",
            "&:hover": { borderColor: "primary.main" },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: "2px",
            } }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "flex-start" }}>
          {task.status === "done" ? (
            <Box component="span" sx={{ color: "success.main", fontSize: 14, mt: "2px" }}>&#10003;</Box>
          ) : null}
          <Typography variant="body2" sx={{ fontWeight: 500,
            textDecoration: task.status === "done" ? "line-through" : "none",
            color: task.status === "done" ? "text.secondary" : "text.primary" }}>
            {task.title}
          </Typography>
        </Stack>
        {task.tags?.length ? (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {task.tags.map((name) => (
              <Chip key={name} size="small" variant="outlined" label={name}
                sx={{ borderColor: tagColor(name) || "divider", height: 20, fontSize: 11 }} />
            ))}
          </Stack>
        ) : null}
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {project.key}-{task.number}
          </Typography>
          {subtaskCount ? (
            <Typography variant="caption" color="text.secondary">☑ {subtaskCount}</Typography>
          ) : null}
          {task.priority ? (
            <Chip size="small" color={PRIORITY_COLOR[task.priority] || "default"}
              variant="outlined" label={task.priority} />
          ) : null}
          {task.tshirtSize ? <Chip size="small" variant="outlined" label={task.tshirtSize} /> : null}
          {epic ? (
            <Chip size="small" variant="outlined" label={epic.name}
              sx={{ borderColor: epic.color || "divider" }} />
          ) : null}
          <Box sx={{ flex: 1 }} />
          {assignee ? (
            <Tooltip title={assignee.displayName || assignee.emails?.[0] || "assignee"}>
              <Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>
                {initials(assignee.displayName || assignee.emails?.[0])}
              </Avatar>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function AddCard({ project, sectionId, onDone }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.createTask(project.id, { title, sectionId });
      setTitle(""); setAdding(false); onDone();
    } finally { setBusy(false); }
  };
  if (!adding) {
    return <Button size="small" onClick={() => setAdding(true)} sx={{ justifyContent: "flex-start" }}>+ Add card</Button>;
  }
  return (
    <Stack spacing={1}>
      <TextField size="small" autoFocus placeholder="Card title" value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setAdding(false); }}
        multiline fullWidth />
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" onClick={submit} disabled={busy || !title.trim()}>Add</Button>
        <Button size="small" onClick={() => { setAdding(false); setTitle(""); }}>Cancel</Button>
      </Stack>
    </Stack>
  );
}

function AddColumn({ project, onDone, onCancel }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.createSection(project.id, { name }); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <TextField size="small" autoFocus placeholder="Column name" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onCancel(); }} />
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={submit} disabled={busy || !name.trim()}>Add</Button>
          <Button size="small" onClick={onCancel}>Cancel</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function DeleteColumn({ project, section, disabled, onDone }) {
  const remove = async () => {
    if (!window.confirm(`Delete column "${section.name}"?`)) return;
    try { await api.deleteSection(project.id, section.id); onDone(); }
    catch (e) { window.alert(e.body?.detail || e.message); }
  };
  return (
    <Tooltip title={disabled ? "Move its cards first" : "Delete column"}>
      <span>
        <IconButton size="small" onClick={remove} disabled={disabled} aria-label="Delete column">
          <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>×</Box>
        </IconButton>
      </span>
    </Tooltip>
  );
}
